/**
 * Fixture for the tokens widget — shape of token_usage's structured
 * output (mcp/server.ts tokenUsageTool): zero-filled 7-day series,
 * per-agent split, totals, and the phase-9 §B2 per-seat rollups
 * (rollupSource "buckets" so the seat section actually renders).
 */
import { MACHINES, USAGE_NOTE, hoursAgo } from "./seed";

const DAY_MS = 24 * 60 * 60 * 1000;

// A believable week: quiet weekend shoulder, heavy mid-week nights.
const WEEK: Array<[number, number]> = [
  [180_000, 41_000],
  [310_000, 64_000],
  [95_000, 22_000],
  [264_000, 58_000],
  [402_000, 81_000],
  [221_000, 47_000],
  [428_000, 97_000],
];

export function tokensFixture(): Record<string, unknown> {
  // Same axis the server builds: last `days` UTC dates, oldest first.
  const days = WEEK.map(([inTok, outTok], i) => ({
    date: new Date(Date.now() - (WEEK.length - 1 - i) * DAY_MS)
      .toISOString()
      .slice(0, 10),
    in: inTok,
    out: outTok,
  }));
  const totals = days.reduce(
    (acc, d) => ({ in: acc.in + d.in, out: acc.out + d.out }),
    { in: 0, out: 0 }
  );

  // Phase-9 §B2 seat rollups (SeatRollup shape, in+out per window).
  const seats = [
    {
      seatId: MACHINES.mini.seatId,
      label: MACHINES.mini.seatLabel,
      machineNames: [MACHINES.mini.name],
      window5h: { in: 310_000, out: 64_000 },
      today: { in: 520_000, out: 98_000 },
      week: { in: 1_380_000, out: 270_000 },
      month: { in: 4_900_000, out: 1_020_000 },
    },
    {
      seatId: MACHINES.air.seatId,
      label: MACHINES.air.seatLabel,
      machineNames: [MACHINES.air.name],
      window5h: { in: 88_000, out: 21_000 },
      today: { in: 142_000, out: 33_000 },
      week: { in: 520_000, out: 140_000 },
      month: { in: 2_100_000, out: 480_000 },
    },
  ];
  const sumWindow = (k: "window5h" | "today" | "week" | "month") =>
    seats.reduce(
      (acc, s) => ({ in: acc.in + s[k].in, out: acc.out + s[k].out }),
      { in: 0, out: 0 }
    );

  return {
    windowDays: 7,
    since: hoursAgo(7 * 24),
    days,
    agents: [
      { agent: "email-triage", in: 1_100_000, out: 210_000 },
      { agent: "morning-brief", in: 440_000, out: 136_000 },
      { agent: "health-sync", in: 360_000, out: 64_000 },
    ],
    totals: { ...totals, sessions: 23 },
    seats,
    seatTotals: {
      window5h: sumWindow("window5h"),
      today: sumWindow("today"),
      week: sumWindow("week"),
      month: sumWindow("month"),
    },
    rollupSource: "buckets",
    timezone: "America/New_York",
    note: USAGE_NOTE,
    teamRef: null,
  };
}
